import {Injectable} from "@angular/core";
import {GridLayoutService} from "@irb/irb-common/interfaces/grid-layout.service";
import {DeleteHeaderComponent} from "@irb/irb-common/components/grid/renderers/delete.header.component";
import {DeleteCellComponent} from "@irb/irb-common/components/grid/renderers/delete.cell.component";
import {NumberTextBoxRendererComponent} from "@irb-common/components/grid/renderers/number-text-box.cell.component";
import {UtilityService} from "@irb/irb-common/services/utility.service";
import {RollCoveredcallSettingsComponent} from "./roll-coveredcall-settings.component";
import {OptionToOpenCellRenderer} from "./option-to-open-cell-renderer.compoent";
import {OptionToCloseRollCellRenderer} from "./option-to-close-cell/option-to-close-roll-cell-renderer.component";

@Injectable({
    providedIn: 'root'
})
export class RollCoveredCallGridLayoutService implements GridLayoutService<RollCoveredcallSettingsComponent> {

    sizeFor: any;

    constructor(private util: UtilityService) {
        this.sizeFor = {
            "underlyingSymbol": 150,
            "optionSymbol": 190,
            "daysToExpiration": 190,
            "percentOfCoverCallToRoll": 220,
            "optionSymbolToRollTo": 190
        };
    }

    getFrameworkComponents() {
        return {
            "DeleteHeaderComponent": DeleteHeaderComponent,
            "DeleteCellComponent": DeleteCellComponent,
            "OptionToCloseRollCellRenderer": OptionToCloseRollCellRenderer,
            "OptionToOpenCellRenderer": OptionToOpenCellRenderer,
            "NumberTextBoxRendererComponent": NumberTextBoxRendererComponent
        };
    }

    isExpirationDaysEditable(params: any) {
        return !params.context.componentParent.isReadOnly && params.data.optionSelectionMode !== 'OPTION_CHAIN';
    }

    isGridEditable(params: any) {
        return !params.context.componentParent.isReadOnly;
    }

    percentOfCoverCallToRollValueGetter(params: any) {
        let val = params.context.componentParent.decimalPipe.transform(params.data.percentOfCoverCallToRoll, '1.2-2');
        return val + " %";
    }

    daysToExpirationValue(params: any) {
        let dte: any;
        if (params.data.optionSelectionMode) {
            switch(params.data.optionSelectionMode) {
                case 'ALL_CALLS':
                    dte = Number(params.data.daysToExpiration);
                    break;
                case 'ALL_PUTS':
                    dte = Number(params.data.daysToExpiration);
                    break;
                case 'OPTION_CHAIN':
                    dte = '-';
                    break;
            }
        }
        return dte;
    }

    daysToExpirationValueSetter(params: any) {
        params.data.daysToExpiration = Number(params.newValue);
        return true;
    }

    optionToCloseValueSetter(params: any) {
        if (params.oldValue !== params.newValue) {
            switch(params.newValue) {
                case 'All Calls':
                    params.data.optionSelectionMode = "ALL_CALLS";
                    params.data.optionSymbol = null;
                    break;
                case 'All Puts':
                    params.data.optionSelectionMode = "ALL_PUTS";
                    params.data.optionSymbol = null;
                    break;
            }
        }
        return true;
    }

    getColumnDefs(parent: RollCoveredcallSettingsComponent) {
        return [
            {
                headerName: 'Delete All',
                headerComponent: 'DeleteHeaderComponent',
                headerComponentParams: {
                    message: "Remove all items?",
                    proceed: parent.deleteAll.bind(parent),
                    cancel: parent.cancelDelete.bind(parent),
                    disableDelete: parent.isReadOnly
                },
                cellRenderer: 'DeleteCellComponent',
                cellRendererParams:  {
                    message: "Remove this item?",
                    proceed: parent.delete.bind(parent),
                    cancel: parent.cancelDelete.bind(parent),
                    disableDelete: this.isGridEditable.bind(this)
                },
                resizable: false,
                minWidth: 25,
                maxWidth: 25,
                width: 25,
                headerHeight: 60,
            },
            {
                headerName: 'Underlying Symbol',
                field: 'underlyingSymbol',
                minWidth: this.sizeFor.underlyingSymbol,
                flex: 1,
                resizable: false,
                sortable: false,
                editable: false,
                suppressMovable: true,
                cellHeight: 30,
            },
            {
                headerName: 'Option to close',
                headerComponentParams: {
                    irbColInfo: `<p>This is the existing option position iRebal will look to close. You can select a specific option or you can use the generic All puts or All calls selections to close or roll your options all together.</p>`
                },
                field: 'optionSymbol',
                valueSetter: this.optionToCloseValueSetter,
                cellClass: 'dropdownFilterCell',
                //editable: this.isGridEditable,
                minWidth: this.sizeFor.optionSymbol,
                flex: 1,
                //cellEditorFramework: OptionToCloseRollCellEditor,
                cellRenderer: 'OptionToCloseRollCellRenderer',
                cellRendererParams: (params: any) => {
                    return {
                        isDisabled: (() => !this.isGridEditable(params)),
                    };
                },
                sortable: false,
                resizable: false,
                suppressMovable: true,
                cellHeight: 30,
            },
            {
                headerName: '< Days to Expiration',
                headerComponentParams: {
                    irbColInfo: `<p>If all puts or all calls are selected in the Option to close column, then the Less than Days to expiration is a mandatory field. Options held that both meet the type of option and have fewer days left to expiration than the input will be closed.</p>`
                },
                field: 'daysToExpiration',
                // valueGetter: this.daysToExpirationValue,
                // valueSetter: this.daysToExpirationValueSetter,
                // cellEditorFramework: DaysToExpirationCellEditor,
                minWidth: this.sizeFor.daysToExpiration,
                flex: 1,
                // editable: this.isExpirationDaysEditable,
                cellClass: "numberTextBoxCell",
                cellRenderer: "NumberTextBoxRendererComponent",
                cellRendererParams: () => {
                    return {
                        min: 1,
                        max: 365,
                        autofocus: true,
                        invalidRangeMessage:"Please enter only numbers between 1 and 365",
                        isPercentage: false,
                        onChange: (params: any, c: any) => {
                            if (params && params.data.daysToExpiration != c.value && c.isValid) {
                                params.data.daysToExpiration = parseFloat(c.value);
                                params.api.redrawRows({ rowNodes: [params.node] });
                            }
                        },
                    };
                },
                resizable: false,
                suppressMovable: true,
                sortable: false
            },
            {
                headerName: 'Percent of position to roll',
                headerComponentParams: {
                    irbColInfo: `<p>Enter a % of the option security to roll. Examples: entering 100% tells iRebal to roll the entire open option position in the account; entering 50% will roll 50% of the option share quantity in the account.</p>`
                },
                field: 'percentOfCoverCallToRoll',
                // valueGetter: this.percentOfCoverCallToRollValueGetter,
                // cellEditorFramework: CoverCallToRollCellEditor,
                minWidth: this.sizeFor.percentOfCoverCallToRoll,
                flex: 1,
                // editable: this.isGridEditable,
                cellClass: "numberTextBoxCell",
                cellRenderer: "NumberTextBoxRendererComponent",
                cellRendererParams: () => {
                    return {
                        min: 0,
                        max: 100,
                        places: 2,
                        isPercentage: true,
                        onChange: (params: any, c: any) => {
                            if (params && params.data.percentOfCoverCallToRoll != c.value && c.isValid) {
                                params.data.percentOfCoverCallToRoll = parseFloat(c.value);
                                params.api.redrawRows({ rowNodes: [params.node] });
                            }
                        },
                    };
                },
                resizable: false,
                suppressMovable: true,
                sortable: false
            },
            {
                headerName: 'Option to open',
                headerComponentParams: {
                    irbColInfo: `<p>This is the new option position iRebal will look to open.</p>`
                },
                field: 'optionSymbolToRollTo',
                editable: false,
                //width: this.sizeFor.optionSymbolToRollTo,
                minWidth: this.sizeFor.optionSymbolToRollTo,
                flex: 1,
                cellRenderer: 'OptionToOpenCellRenderer',
                sortable: false,
                resizable: false,
                suppressMovable: true,
                cellHeight: 30,
            }
        ];
    }

}
